import type { Edge, HandleType } from '@xyflow/react'
import { addEdge, createEdgeId, createLinkedNodeFromSource, createNode, flowStore, removeEdge } from '@/entities/flow'
import { getEdgesToReplaceForTarget } from '../../lib'
import { requestOpenNodeOpEditor } from '../../lib/node-op-edit-intent-store'
import type { NodeMoreOpItem } from './types'

// 文件说明：封装从 toolbar 或连接引导创建下游 OP 节点的公共逻辑。

const DEFAULT_NODE_SIZE = {
    width: 260,
    height: 148,
}

type GetInternalNode = (nodeId: string) => { measured?: { width?: number; height?: number } } | undefined

// 构造新 OP 节点需要写入节点数据的基础字段。
function buildOpCustomData(
    item: NodeMoreOpItem,
    initialOpData?: Record<string, unknown>,
    sourceNodeIds: string[] = [],
) {
    const createData = item.createCustomData?.({
        sourceNodeIds,
        targetNodeType: item.nodeType,
    }) ?? {}

    return {
        ...createData,
        title: item.label,
        toolbar: item.toolbar ?? '',
        op: item.op,
        ...(initialOpData ? {
            op_data: {
                [item.op]: initialOpData,
            },
        } : {}),
    }
}

// 根据菜单项配置决定是否在新节点渲染后自动打开编辑弹框。
function requestAutoOpenEditorIfNeeded(item: NodeMoreOpItem, nodeId: string, autoOpenEditor?: boolean) {
    const shouldOpen = autoOpenEditor ?? item.autoOpenEditor
    if (shouldOpen) {
        requestOpenNodeOpEditor(nodeId)
    }
}

// 从节点 toolbar 创建下游 OP 节点，并按菜单声明写入自动打开意图。
export function createLinkedOpNodeFromToolbar({
    autoOpenEditor,
    edgeData,
    sourceNodeId,
    item,
    getInternalNode,
    initialOpData,
}: {
    autoOpenEditor?: boolean
    edgeData?: Edge['data']
    sourceNodeId: string
    item: NodeMoreOpItem
    getInternalNode?: GetInternalNode
    initialOpData?: Record<string, unknown>
}) {
    const result = createLinkedNodeFromSource({
      sourceNodeId,
      type: item.nodeType,
      pos: 'down',
      getInternalNode,
      customData: buildOpCustomData(item, initialOpData, [sourceNodeId]),
      ...(edgeData ? {
        edgeData,
        sourceHandle: 'canvas-source-logical',
        targetHandle: 'canvas-target-logical',
      } : {}),
    })

    requestAutoOpenEditorIfNeeded(item, result.nodeId, autoOpenEditor)
    return result
}

// 根据连接引导落点和 handle 类型计算新节点位置。
function resolveGuidePosition({
    flowPosition,
    handleType,
}: {
    flowPosition: { x: number; y: number }
    handleType: HandleType
}) {
    return {
        x: handleType === 'source' ? flowPosition.x : flowPosition.x - DEFAULT_NODE_SIZE.width,
        y: flowPosition.y - DEFAULT_NODE_SIZE.height / 2,
    }
}

// 从连接引导创建 OP 节点和边，并按菜单声明写入自动打开意图；edgeData 用于 slot reference 等需要写业务 context 的连接。
export function createLinkedOpNodeFromConnectionGuide({
    anchorNodeId,
    autoOpenEditor,
    edgeData,
    item,
    flowPosition,
    handleType,
    initialOpData,
}: {
    anchorNodeId: string
    autoOpenEditor?: boolean
    edgeData?: Edge['data']
    item: NodeMoreOpItem
    flowPosition: { x: number; y: number }
    handleType: HandleType
    initialOpData?: Record<string, unknown>
}) {
    const position = resolveGuidePosition({ flowPosition, handleType })
    const { id: newNodeId } = createNode({
        type: item.nodeType,
        position,
        customData: buildOpCustomData(item, initialOpData, [anchorNodeId]),
    })

    const sourceId = handleType === 'source' ? anchorNodeId : newNodeId
    const targetId = handleType === 'source' ? newNodeId : anchorNodeId
    const edgeId = createEdgeId({ source: sourceId, target: targetId })

    // 提前计算超限挤出逻辑（在新边添加之前计算）
    const edgesToReplace = getEdgesToReplaceForTarget({
        edges: flowStore.edges,
        nodes: flowStore.nodes,
        target: targetId,
    })

    addEdge({
        id: edgeId,
        source: sourceId,
        target: targetId,
        ...(edgeData ? { data: edgeData } : {}),
        sourceHandle: 'canvas-source-logical',
        targetHandle: 'canvas-target-logical',
    })

    // 移除计算出的多余旧边
    edgesToReplace.forEach(edge => removeEdge(edge.id))

    requestAutoOpenEditorIfNeeded(item, newNodeId, autoOpenEditor)

    return {
        nodeId: newNodeId,
        edgeId,
    }
}
